import * as DialogPrimitive from '@radix-ui/react-dialog'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'

interface ImageGalleryProps {
  images: string[]
  /** Names the entity for image alt text and the dialog label. */
  label: string
  className?: string
}

interface LightboxState {
  index: number
  /** Slide direction of the last navigation: 1 forward, -1 back, 0 initial. */
  direction: number
}

const SPRING_EASE = [0.16, 1, 0.3, 1] as const

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 48 : direction < 0 ? -48 : 0,
    opacity: 0,
    scale: 0.98,
  }),
  center: { x: 0, opacity: 1, scale: 1 },
  exit: (direction: number) => ({
    x: direction > 0 ? -48 : direction < 0 ? 48 : 0,
    opacity: 0,
    scale: 0.98,
  }),
}

export function ImageGallery({ images, label, className }: ImageGalleryProps) {
  const [lightbox, setLightbox] = useState<LightboxState | null>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const open = lightbox !== null
  const hasMultiple = images.length > 1
  // Clamp so a background refetch that returns fewer images can't leave the
  // lightbox pointing past the end (undefined src / key).
  const activeIndex = lightbox ? Math.min(lightbox.index, images.length - 1) : 0

  const step = useCallback(
    (delta: number) => {
      setLightbox((current) =>
        current
          ? { index: (current.index + delta + images.length) % images.length, direction: delta }
          : current,
      )
    },
    [images.length],
  )

  useEffect(() => {
    if (!open || !hasMultiple) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') step(1)
      else if (event.key === 'ArrowLeft') step(-1)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, hasMultiple, step])

  if (images.length === 0) return null

  return (
    <>
      <div className={cn('grid grid-cols-4 gap-2 sm:grid-cols-5', className)}>
        {images.map((url, index) => (
          <button
            key={url}
            type="button"
            aria-label={m.galleryOpenImage({ number: index + 1 })}
            onClick={() => setLightbox({ index, direction: 0 })}
            className="group relative aspect-square cursor-zoom-in overflow-hidden rounded-md border border-border/60 outline-none focus-visible:ring-2 focus-visible:ring-brand/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <img
              src={url}
              alt={`${label} ${index + 1}`}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"
            />
            <span className="pointer-events-none absolute inset-0 bg-foreground/0 transition-colors duration-200 group-hover:bg-foreground/10" />
          </button>
        ))}
      </div>

      <DialogPrimitive.Root open={open} onOpenChange={(next) => !next && setLightbox(null)}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            onOpenAutoFocus={(event) => {
              event.preventDefault()
              closeRef.current?.focus()
            }}
            className="fixed inset-0 z-50 outline-none"
          >
            <DialogPrimitive.Title className="sr-only">{label}</DialogPrimitive.Title>

            {/* Full-screen backdrop; clicking it (or its gaps around the image) closes. */}
            <DialogPrimitive.Close
              aria-label={m.galleryClose()}
              className="absolute inset-0 cursor-zoom-out outline-none"
            />

            {lightbox && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-4 sm:p-14">
                <AnimatePresence initial={false} custom={lightbox.direction} mode="popLayout">
                  {hasMultiple ? (
                    <motion.button
                      type="button"
                      key={activeIndex}
                      aria-label={m.galleryNext()}
                      onClick={() => step(1)}
                      custom={lightbox.direction}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.32, ease: SPRING_EASE }}
                      className="pointer-events-auto flex max-h-full max-w-full cursor-pointer items-center justify-center outline-none"
                    >
                      <img
                        src={images[activeIndex]}
                        alt={`${label} ${activeIndex + 1}`}
                        draggable={false}
                        className="max-h-full max-w-full rounded-lg object-contain"
                      />
                    </motion.button>
                  ) : (
                    <motion.img
                      key={activeIndex}
                      src={images[activeIndex]}
                      alt={`${label} ${activeIndex + 1}`}
                      draggable={false}
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.32, ease: SPRING_EASE }}
                      className="max-h-full max-w-full rounded-lg object-contain"
                    />
                  )}
                </AnimatePresence>
              </div>
            )}

            <DialogPrimitive.Close
              ref={closeRef}
              aria-label={m.galleryClose()}
              className="absolute top-4 right-4 flex size-10 items-center justify-center rounded-full bg-white/10 text-white outline-none transition-colors hover:bg-white/20 focus-visible:ring-2 focus-visible:ring-white/70"
            >
              <X className="size-5" />
            </DialogPrimitive.Close>

            {hasMultiple && lightbox && (
              <>
                <button
                  type="button"
                  aria-label={m.galleryPrevious()}
                  onClick={() => step(-1)}
                  className="absolute top-1/2 left-2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white outline-none transition-colors hover:bg-white/20 focus-visible:ring-2 focus-visible:ring-white/70 sm:left-4"
                >
                  <ChevronLeft className="size-6" />
                </button>
                <button
                  type="button"
                  aria-label={m.galleryNext()}
                  onClick={() => step(1)}
                  className="absolute top-1/2 right-2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white outline-none transition-colors hover:bg-white/20 focus-visible:ring-2 focus-visible:ring-white/70 sm:right-4"
                >
                  <ChevronRight className="size-6" />
                </button>
                <p className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 font-medium text-sm text-white tabular-nums">
                  {m.galleryPosition({ current: activeIndex + 1, total: images.length })}
                </p>
              </>
            )}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  )
}
